import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  ButtonSecondary,
  ButtonSize,
  IconLoader,
  IconUp,
  IconDown,
  Textarea,
  ButtonPseudo,
  Popup,
  IconChangeLoClock,
} from '@ui/src';
import { CustomerCommentsTypes } from './types';
import CustomerComment from './comment';

import cn from './comments.module.css';

const CustomerComments = ({
  comments,
  pagination,
  isLoading,
  isFetching,
  getCommentsList,
  createAvailable,
  editAvailable,
  deleteAvailable,
  onAddCommentToCustomer,
  addCommentInProgress,
  addCommentIsSuccess,
  onEditCommentInCustomer,
  editCommentInProgress,
  editCommentIsSuccess,
  users,
  onDeleteCommentInCustomer,
  deleteCommentInProgress,
  deleteCommentIsSuccess,
  changeLogsAvailable,
}: CustomerCommentsTypes) => {
  const [expanded, setExpanded] = useState(false);
  const [textareaVisible, setTextareaVisible] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [
    deleteCommentConfirmationVisible,
    setDeleteCommentConfirmationVisible,
  ] = useState(false);
  const [selectedComment, setSelectedComment] = useState('');

  useEffect(() => {
    if (addCommentIsSuccess) {
      setCommentText('');
      setTextareaVisible(false);
    }
  }, [addCommentIsSuccess]);

  useEffect(() => {
    if (deleteCommentIsSuccess) {
      setDeleteCommentConfirmationVisible(false);
      setSelectedComment('');
    }
  }, [deleteCommentIsSuccess]);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.contentLoader}>
        <IconLoader />
      </div>
    );
  } else if (!isLoading && !comments?.length) {
    content = <div className={cn.emptyComments}>There are no comments</div>;
  } else if (!isLoading && comments) {
    content = (
      <>
        {comments?.map((item) => (
          <CustomerComment
            key={item.id}
            comment={item}
            onEditCommentInCustomer={onEditCommentInCustomer}
            editCommentIsSuccess={editCommentIsSuccess}
            editCommentInProgress={editCommentInProgress}
            editAvailable={editAvailable}
            deleteAvailable={deleteAvailable}
            user={users?.find((user) => user.value === item?.userId)}
            setDeleteCommentConfirmationVisible={
              setDeleteCommentConfirmationVisible
            }
            setSelectedComment={setSelectedComment}
          />
        ))}
        {pagination?.hasNextPage && (
          <div className={cn.paginationWrapper}>
            <ButtonSecondary
              text={isFetching ? 'Loading...' : 'Show more'}
              size={ButtonSize.SMALL}
              className="button-white"
              onClick={() => getCommentsList(pagination?.endCursor)}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className={cn.commentsWrapper}>
      <button
        type="button"
        className={cn.commentsExpandButton}
        onClick={() => setExpanded(!expanded)}
      >
        <div className={cn.commentsExpandButtonTitle}>{`Comments (${
          pagination?.totalCount || 0
        })`}</div>
        <div className={cn.commentsExpandButtonIcon}>
          {expanded ? (
            <IconUp width={14} height={11} />
          ) : (
            <IconDown width={15} height={11} />
          )}
        </div>
      </button>
      {expanded && (
        <>
          <div className={cn.commentsTop}>
            <div
              className={`${cn.commentsTopWrapper} ${
                !createAvailable || textareaVisible ? cn.flexEnd : ''
              }`}
            >
              {createAvailable && !textareaVisible && (
                <ButtonSecondary
                  size={ButtonSize.SMALL}
                  text="Add comment"
                  onClick={() => setTextareaVisible(true)}
                  className={cn.createCommentButton}
                />
              )}
              {changeLogsAvailable && (
                <NavLink
                  to="/administration/change-logs?entityType=ORGANIZATION_COMMENT"
                  target="_blank"
                  className={cn.changeLogLink}
                >
                  <IconChangeLoClock />
                  Change Log
                </NavLink>
              )}
            </div>

            {textareaVisible && (
              <div className={cn.textareaWrapper}>
                <Textarea
                  name="commentText"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Enter comment..."
                  className={cn.commentTextarea}
                />
                <div className={cn.createCommentControls}>
                  <ButtonSecondary
                    size={ButtonSize.SMALL}
                    className={`button-white ${cn.sendCommentButton}`}
                    text={addCommentInProgress ? 'Loading...' : 'Save comment'}
                    disabled={addCommentInProgress}
                    onClick={() => onAddCommentToCustomer(commentText)}
                  />
                  <ButtonPseudo
                    size={ButtonSize.SMALL}
                    text="Cancel"
                    onClick={() => {
                      setCommentText('');
                      setTextareaVisible(false);
                    }}
                    disabled={addCommentInProgress}
                  />
                </div>
              </div>
            )}
          </div>
          <div className={cn.commentsItemsList}>{content}</div>
        </>
      )}

      <Popup
        title="Delete comment?"
        onCancelClick={() => setDeleteCommentConfirmationVisible(false)}
        onOkClick={() => onDeleteCommentInCustomer(selectedComment)}
        cancelButtonText="Cancel"
        okButtonText={deleteCommentInProgress ? 'Loading...' : 'Delete'}
        isVisible={deleteCommentConfirmationVisible}
        isLoading={deleteCommentInProgress}
      />
    </div>
  );
};

export default CustomerComments;
